import * as i0 from '@angular/core';
import { Injectable, booleanAttribute, forwardRef, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, inject, ViewChild, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ReplaySubject, Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i2 from '@angular/cdk/bidi';
import { Directionality } from '@angular/cdk/bidi';
import { NzFormStatusService } from 'ng-zorro-antd/core/form';
import * as i1 from '@angular/cdk/a11y';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["*"];
const _c1 = ["inputElement"];
const _c2 = ["nz-radio", ""];
let NzRadioService = /*#__PURE__*/(() => {
  class NzRadioService {
    constructor() {
      this.selected$ = new ReplaySubject(1);
      this.touched$ = new Subject();
      this.disabled$ = new ReplaySubject(1);
      this.name$ = new ReplaySubject(1);
    }
    touch() {
      this.touched$.next();
    }
    select(value) {
      this.selected$.next(value);
    }
    setDisabled(value) {
      this.disabled$.next(value);
    }
    setName(value) {
      this.name$.next(value);
    }
    static {
      this.ɵfac = function NzRadioService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzRadioService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NzRadioService,
        factory: NzRadioService.ɵfac
      });
    }
  }
  return NzRadioService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzRadioGroupComponent = /*#__PURE__*/(() => {
  class NzRadioGroupComponent {
    constructor(cdr, nzRadioService, directionality) {
      this.cdr = cdr;
      this.nzRadioService = nzRadioService;
      this.directionality = directionality;
      this.value = null;
      this.destroy$ = new Subject();
      this.isNzDisableFirstChange = true;
      this.onChange = () => {};
      this.onTouched = () => {};
      this.nzDisabled = false;
      this.nzButtonStyle = 'outline';
      this.nzSize = 'default';
      this.nzName = null;
      this.dir = 'ltr';
    }
    ngOnInit() {
      this.nzRadioService.selected$.pipe(takeUntil(this.destroy$)).subscribe(value => {
        if (this.value !== value) {
          this.value = value;
          this.onChange(this.value);
        }
      });
      this.nzRadioService.touched$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        Promise.resolve().then(() => this.onTouched());
      });
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.directionality.value;
    }
    ngOnChanges(changes) {
      const {
        nzDisabled,
        nzName
      } = changes;
      if (nzDisabled) {
        this.nzRadioService.setDisabled(this.nzDisabled);
      }
      if (nzName) {
        this.nzRadioService.setName(this.nzName);
      }
    }
    ngOnDestroy() {
      this.destroy$.next(true);
      this.destroy$.complete();
    }
    writeValue(value) {
      this.value = value;
      this.nzRadioService.select(value);
      this.cdr.markForCheck();
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
      this.nzDisabled = this.isNzDisableFirstChange && this.nzDisabled || isDisabled;
      this.isNzDisableFirstChange = false;
      this.nzRadioService.setDisabled(this.nzDisabled);
      this.cdr.markForCheck();
    }
    static {
      this.ɵfac = function NzRadioGroupComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzRadioGroupComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(NzRadioService), i0.ɵɵdirectiveInject(i2.Directionality));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzRadioGroupComponent,
        selectors: [["nz-radio-group"]],
        hostAttrs: [1, "ant-radio-group"],
        hostVars: 8,
        hostBindings: function NzRadioGroupComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-radio-group-large", ctx.nzSize === "large")("ant-radio-group-small", ctx.nzSize === "small")("ant-radio-group-solid", ctx.nzButtonStyle === "solid")("ant-radio-group-rtl", ctx.dir === "rtl");
          }
        },
        inputs: {
          nzDisabled: [2, "nzDisabled", "nzDisabled", booleanAttribute],
          nzButtonStyle: "nzButtonStyle",
          nzSize: "nzSize",
          nzName: "nzName"
        },
        exportAs: ["nzRadioGroup"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([NzRadioService, {
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => NzRadioGroupComponent),
          multi: true
        }]), i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c0,
        decls: 1,
        vars: 0,
        template: function NzRadioGroupComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzRadioGroupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzRadioComponent = /*#__PURE__*/(() => {
  class NzRadioComponent {
    focus() {
      this.focusMonitor.focusVia(this.inputElement, 'keyboard');
    }
    blur() {
      this.inputElement.nativeElement.blur();
    }
    constructor(ngZone, elementRef, cdr, focusMonitor) {
      this.ngZone = ngZone;
      this.elementRef = elementRef;
      this.cdr = cdr;
      this.focusMonitor = focusMonitor;
      this.isNgModel = false;
      this.destroy$ = new Subject();
      this.isNzDisableFirstChange = true;
      this.directionality = inject(Directionality);
      this.nzRadioService = inject(NzRadioService, {
        optional: true
      });
      this.nzFormStatusService = inject(NzFormStatusService, {
        optional: true
      });
      this.isChecked = false;
      this.name = null;
      this.onChange = () => {};
      this.onTouched = () => {};
      this.nzValue = null;
      this.nzDisabled = false;
      this.nzAutoFocus = false;
      this.isRadioButton = false;
      this.dir = 'ltr';
    }
    setDisabledState(disabled) {
      this.nzDisabled = this.isNzDisableFirstChange && this.nzDisabled || disabled;
      this.isNzDisableFirstChange = false;
      this.cdr.markForCheck();
    }
    writeValue(value) {
      this.isChecked = value;
      this.cdr.markForCheck();
    }
    registerOnChange(fn) {
      this.isNgModel = true;
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    ngOnInit() {
      if (this.nzRadioService) {
        this.nzRadioService.name$.pipe(takeUntil(this.destroy$)).subscribe(name => {
          this.name = name;
          this.cdr.markForCheck();
        });
        this.nzRadioService.disabled$.pipe(takeUntil(this.destroy$)).subscribe(disabled => {
          this.nzDisabled = this.isNzDisableFirstChange && this.nzDisabled || disabled;
          this.isNzDisableFirstChange = false;
          this.cdr.markForCheck();
        });
        this.nzRadioService.selected$.pipe(takeUntil(this.destroy$)).subscribe(value => {
          const isChecked = this.isChecked;
          this.isChecked = this.nzValue === value;
          // We don't have to run `onChange()` on each `nz-radio` button whenever the `selected$` emits.
          // If we have 8 `nz-radio` buttons within the `nz-radio-group` and they're all connected with
          // `ngModel` or `formControl` then `onChange()` will be called 8 times for each `nz-radio` button.
          // We prevent this by checking if `isChecked` has been changed or not.
          if (this.isNgModel && isChecked !== this.isChecked &&
          // We're only intereted if `isChecked` has been changed to `false` value to emit `false` to the ascendant form,
          // since we already emit `true` within the `setupClickListener`.
          this.isChecked === false) {
            this.onChange(false);
          }
          this.cdr.markForCheck();
        });
      }
      this.focusMonitor.monitor(this.elementRef, true).pipe(takeUntil(this.destroy$)).subscribe(focusOrigin => {
        if (!focusOrigin) {
          Promise.resolve().then(() => this.onTouched());
          if (this.nzRadioService) {
            this.nzRadioService.touch();
          }
        }
      });
      this.directionality.change.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.directionality.value;
      this.setupClickListener();
    }
    ngAfterViewInit() {
      if (this.nzAutoFocus) {
        this.focus();
      }
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
      this.focusMonitor.stopMonitoring(this.elementRef);
    }
    setupClickListener() {
      this.ngZone.runOutsideAngular(() => {
        fromEvent(this.elementRef.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(event => {
          /** prevent label click triggered twice. **/
          event.stopPropagation();
          event.preventDefault();
          if (this.nzDisabled || this.isChecked) {
            return;
          }
          this.ngZone.run(() => {
            this.focus();
            this.nzRadioService?.select(this.nzValue);
            if (this.isNgModel) {
              this.isChecked = true;
              this.onChange(true);
            }
            this.cdr.markForCheck();
          });
        });
      });
    }
    static {
      this.ɵfac = function NzRadioComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzRadioComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.FocusMonitor));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzRadioComponent,
        selectors: [["", "nz-radio", ""], ["", "nz-radio-button", ""]],
        viewQuery: function NzRadioComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c1, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputElement = _t.first);
          }
        },
        hostVars: 18,
        hostBindings: function NzRadioComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-radio-wrapper-in-form-item", !!ctx.nzFormStatusService)("ant-radio-wrapper", !ctx.isRadioButton)("ant-radio-button-wrapper", ctx.isRadioButton)("ant-radio-wrapper-checked", ctx.isChecked && !ctx.isRadioButton)("ant-radio-button-wrapper-checked", ctx.isChecked && ctx.isRadioButton)("ant-radio-wrapper-disabled", ctx.nzDisabled && !ctx.isRadioButton)("ant-radio-button-wrapper-disabled", ctx.nzDisabled && ctx.isRadioButton)("ant-radio-wrapper-rtl", !ctx.isRadioButton && ctx.dir === "rtl")("ant-radio-button-wrapper-rtl", ctx.isRadioButton && ctx.dir === "rtl");
          }
        },
        inputs: {
          nzValue: "nzValue",
          nzDisabled: [2, "nzDisabled", "nzDisabled", booleanAttribute],
          nzAutoFocus: [2, "nzAutoFocus", "nzAutoFocus", booleanAttribute],
          isRadioButton: [2, "nz-radio-button", "isRadioButton", booleanAttribute]
        },
        exportAs: ["nzRadio"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => NzRadioComponent),
          multi: true
        }]), i0.ɵɵInputTransformsFeature, i0.ɵɵStandaloneFeature],
        attrs: _c2,
        ngContentSelectors: _c0,
        decls: 6,
        vars: 24,
        consts: [["inputElement", ""], ["type", "radio", 3, "disabled", "checked"]],
        template: function NzRadioComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "span");
            i0.ɵɵelement(1, "input", 1, 0)(3, "span");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(4, "span");
            i0.ɵɵprojection(5);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵclassProp("ant-radio", !ctx.isRadioButton)("ant-radio-checked", ctx.isChecked && !ctx.isRadioButton)("ant-radio-disabled", ctx.nzDisabled && !ctx.isRadioButton)("ant-radio-button", ctx.isRadioButton)("ant-radio-button-checked", ctx.isChecked && ctx.isRadioButton)("ant-radio-button-disabled", ctx.nzDisabled && ctx.isRadioButton);
            i0.ɵɵadvance();
            i0.ɵɵclassProp("ant-radio-input", !ctx.isRadioButton)("ant-radio-button-input", ctx.isRadioButton);
            i0.ɵɵproperty("disabled", ctx.nzDisabled)("checked", ctx.isChecked);
            i0.ɵɵattribute("autofocus", ctx.nzAutoFocus ? "autofocus" : null)("name", ctx.name);
            i0.ɵɵadvance(2);
            i0.ɵɵclassProp("ant-radio-inner", !ctx.isRadioButton)("ant-radio-button-inner", ctx.isRadioButton);
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzRadioComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzRadioModule = /*#__PURE__*/(() => {
  class NzRadioModule {
    static {
      this.ɵfac = function NzRadioModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzRadioModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzRadioModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return NzRadioModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzRadioComponent, NzRadioGroupComponent, NzRadioModule, NzRadioService };
